import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import sessionStorageStore from 'store/storages/sessionStorage';

import { folderHasChildrenByTotals } from '../../../../util/album-folder';
import WebGalleryAlbumCard from '../WebGalleryAlbumCard/WebGalleryAlbumCard';
import WebGalleryAlbumFolderNullState from '../WebGalleryAlbumFolderNullState';
import WebGalleryActionBar from '../WebGalleryActionBar';
import WebGalleryAlbumFolderCard from '../WebGalleryAlbumFolderCard';
import Spinner from '../../../../components/global/Spinner';
import WebGalleryAlbumFolderHeader from './components/WebGalleryAlbumFolderHeader';
import WebGalleryStickyUpsellBanner from '../WebGalleryStickyUpsellBanner';
import { shouldDisplayUpsellBanner } from '../WebGalleryStickyUpsellBanner/helpers';
import useDismissiblePersistent from '../../../../hooks/useDismissiblePersistent';
import {
  WG_BOTTOM_APP_DOWNLOAD_BANNER,
  WG_BOTTOM_BANNER_UPSELL,
} from '../../../../constants/dismissibleKeys';
import AppDownloadBanner from '../../../../components/global/AppDownloadBanner';
import { shouldDisplayAppDownloadBanner } from '../../../../components/global/AppDownloadBanner/helpers';
import { supportsAutoJoinNullState } from '../../../pro/views/HierarchyItem/helpers';
import WebGalleryAutoJoinNullState from './components/WebGalleryAutoJoinNullState';

const propTypes = {
  loadingAlbumFolder: PropTypes.bool,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    is_web_gallery: PropTypes.bool,
    is_private_web_gallery: PropTypes.bool,
    parent: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    account: PropTypes.shape({
      hero_image: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
      }),
      slug: PropTypes.string,
    }).isRequired,
    children: PropTypes.shape({
      album_folders: PropTypes.shape({
        total: PropTypes.number,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string,
          }),
        ),
      }),
      albums: PropTypes.shape({
        total: PropTypes.number,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string,
            cover_photo: PropTypes.object,
          }),
        ),
      }),
    }),
    root: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  authenticated: PropTypes.bool,
};

const WebGalleryAlbumFolder = ({
  albumFolder,
  loadingAlbumFolder,
  authenticated,
}) => {
  const { onDismiss, visible } = useDismissiblePersistent(
    WG_BOTTOM_BANNER_UPSELL,
  );
  const {
    onDismiss: onDismissAppDownloadBanner,
    visible: visibleAppDownloadBanner,
    /*
     * Banner should always show on a per-session basis for unauthed users,
     * once per user for authed.
     */
  } = useDismissiblePersistent(
    WG_BOTTOM_APP_DOWNLOAD_BANNER,
    authenticated ? undefined : sessionStorageStore,
  );
  const shouldDisplayAppDownloadBannerBool = shouldDisplayAppDownloadBanner(
    albumFolder?.root,
  );

  if (loadingAlbumFolder && !albumFolder) {
    return (
      <div className="p-5 d-flex">
        <Spinner className="m-auto" />
      </div>
    );
  }

  const albumFolderChildren = (
    albumFolder?.children?.album_folders?.records ?? []
  ).filter(childAlbumFolder => folderHasChildrenByTotals(childAlbumFolder));
  const albums = albumFolder?.children?.albums?.records ?? [];
  const hasChildren = albums.length > 0 || albumFolderChildren.length > 0;
  const heroImage =
    albumFolder?.hero_image ||
    albumFolder?.root?.hero_image ||
    albumFolder?.account?.hero_image;

  const wrapperClasses = classNames({
    'flex-auto d-flex flex-column align-content-center mb-4': true,
  });

  if (albumFolder?.individual_albums) {
    return (
      <Redirect
        to={`/galleries/view/album/${albumFolder.children.albums.records[0].id}`}
      />
    );
  }

  const displayAutoJoinNullState = supportsAutoJoinNullState(albumFolder);

  if (displayAutoJoinNullState && !albumFolder?.parent) {
    return (
      <>
        <div className={wrapperClasses}>
          {heroImage && (
            <div className="web-gallery-hero-image">
              <img src={heroImage.url} alt="" />
            </div>
          )}

          {!albumFolder.parent && (
            <Fragment>
              <WebGalleryAlbumFolderHeader
                albumFolder={albumFolder}
                className="text-center mt-3 mb-5"
              />
            </Fragment>
          )}

          <WebGalleryAutoJoinNullState entity={albumFolder} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={wrapperClasses}>
        {heroImage && (
          <div className="web-gallery-hero-image">
            <img src={heroImage.url} alt="" />
          </div>
        )}

        <div>
          {!albumFolder.parent && (
            <Fragment>
              <WebGalleryAlbumFolderHeader
                albumFolder={albumFolder}
                className="text-center mt-3 mb-2"
              />

              {!hasChildren && <WebGalleryAlbumFolderNullState />}
            </Fragment>
          )}

          {hasChildren && <WebGalleryActionBar albumFolder={albumFolder} />}

          <div className={loadingAlbumFolder ? 'block-loading' : ''}>
            {albumFolderChildren.length > 0 && (
              <div className="album-folder-children-container">
                {albumFolderChildren.map(childAlbumFolder => (
                  <WebGalleryAlbumFolderCard
                    key={childAlbumFolder.id}
                    albumFolder={childAlbumFolder}
                  />
                ))}
              </div>
            )}

            {albums.length > 0 && (
              <div className="album-folder-children-container">
                {albums.map(album => {
                  return (
                    <WebGalleryAlbumCard
                      key={album.id}
                      album={album}
                      eventSettings={albumFolder?.event_settings}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {albumFolder &&
          shouldDisplayUpsellBanner(albumFolder?.root) &&
          !visibleAppDownloadBanner &&
          !displayAutoJoinNullState &&
          visible && (
            <WebGalleryStickyUpsellBanner
              albumFolder={albumFolder.root}
              onDismiss={onDismiss}
            />
          )}

        {albumFolder &&
          shouldDisplayAppDownloadBannerBool &&
          !displayAutoJoinNullState &&
          visibleAppDownloadBanner && (
            // Prioritize App Download Banner over Upsell since this one blacks screen
            <AppDownloadBanner onDismiss={onDismissAppDownloadBanner} />
          )}
      </div>
    </>
  );
};

WebGalleryAlbumFolder.propTypes = propTypes;

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

export { WebGalleryAlbumFolder as WebGalleryAlbumFolderComponent };
export default connect(mapStateToProps)(WebGalleryAlbumFolder);
